import { useEffect, useState } from "react";
import { ClipboardList, Plus, Trash2, Eye, EyeOff, Save, RotateCcw } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";

// Super-Admin management of the GLOBAL Daily Tracking List (Hygiene +
// Cleanliness). Edits apply to every tenant's NEW check forms. Existing
// records are unaffected — labels are snapshotted into each record at submit.
export default function ChecklistTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/checklist-templates");
      // VISITOR_REG + VEHICLE_CLEAN_* have their own dedicated admin pages
      // (Visitor Registration Form, Daily Vehicle Cleanliness) — hide them
      // here so the Daily Tracking editor stays focused on the hygiene +
      // cleanliness lists.
      setTemplates((data.templates || []).filter((t) =>
        t.code !== "VISITOR_REG" &&
        t.code !== "VEHICLE_CLEAN_DELIVERY" &&
        t.code !== "VEHICLE_CLEAN_COLD"
      ));
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="vb-fade-in" data-testid="checklist-templates-page">
      <div className="flex items-center gap-3 mb-2">
        <ClipboardList size={26} style={{ color: "var(--vb-accent)" }} />
        <h1 className="vb-heading text-3xl" style={{ color: "var(--vb-text-2)" }}>Daily Tracking List</h1>
      </div>
      <p className="opacity-70 mb-6 max-w-3xl">
        Manage the global <strong>Hygiene</strong> and <strong>Cleanliness</strong> checklists used by every tenant.
        Add or rename items (English + 中文), organise them into groups, and deactivate items you no longer want on
        new check forms. Deactivating never deletes history — past records and audit PDFs keep the exact wording they
        were signed off against.
      </p>

      {err && <div className="vb-pill err mb-4" data-testid="checklist-error">{err}</div>}

      {loading ? (
        <div className="opacity-60" data-testid="checklist-loading">Loading…</div>
      ) : (
        <div className="space-y-8">
          {templates.map((t) => (
            <TemplateEditor key={t.code} template={t} onSaved={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function cloneSections(sections) {
  return (sections || []).map((s) => ({
    name: s.name || "",
    name_zh: s.name_zh || "",
    sub_area: s.sub_area ?? null,
    items: (s.items || []).map((it) => ({
      key: it.key || "",
      label: it.label || "",
      label_zh: it.label_zh || "",
      requires_reason: !!it.requires_reason,
      is_active: it.is_active !== false,
    })),
  }));
}

export function TemplateEditor({ template, onSaved, showRequiresReason = false }) {
  const [title, setTitle] = useState(template.title || "");
  const [formRef, setFormRef] = useState(template.form_ref || "");
  const [sections, setSections] = useState(() => cloneSections(template.sections));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const reset = () => {
    setTitle(template.title || "");
    setFormRef(template.form_ref || "");
    setSections(cloneSections(template.sections));
    setErr("");
  };

  const setItem = (si, ii, patch) =>
    setSections((prev) => prev.map((s, i) => i !== si ? s : {
      ...s, items: s.items.map((it, j) => j !== ii ? it : { ...it, ...patch }),
    }));

  const addItem = (si) =>
    setSections((prev) => prev.map((s, i) => i !== si ? s : {
      ...s, items: [...s.items, { key: "", label: "", label_zh: "", requires_reason: false, is_active: true }],
    }));

  const removeItem = (si, ii) =>
    setSections((prev) => prev.map((s, i) => i !== si ? s : {
      ...s, items: s.items.filter((_, j) => j !== ii),
    }));

  const setGroupName = (si, patch) =>
    setSections((prev) => prev.map((s, i) => i !== si ? s : { ...s, ...patch }));

  const activeCount = sections.reduce((n, s) => n + s.items.filter((it) => it.is_active && it.label.trim()).length, 0);

  const save = async () => {
    setErr("");
    if (!title.trim()) { setErr("Title is required"); return; }
    for (const s of sections) {
      if (!s.name.trim()) { setErr("Every group needs a name"); return; }
      const kept = s.items.filter((it) => it.label.trim());
      if (kept.length === 0) { setErr(`Group "${s.name}" must have at least one item`); return; }
      const active = kept.filter((it) => it.is_active !== false);
      if (active.length === 0) {
        setErr(`Group "${s.name}" needs at least one active item — it would otherwise vanish from check forms.`);
        return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        form_ref: formRef.trim(),
        sections: sections.map((s) => ({
          name: s.name.trim(),
          name_zh: (s.name_zh || "").trim(),
          sub_area: s.sub_area ?? null,
          items: s.items
            .filter((it) => it.label.trim())
            .map((it) => ({
              key: it.key || "",
              label: it.label.trim(),
              label_zh: (it.label_zh || "").trim(),
              requires_reason: !!it.requires_reason,
              is_active: it.is_active !== false,
            })),
        })),
      };
      await api.put(`/admin/checklist-templates/${template.code}`, payload);
      toast.success(`${title.trim()} saved — applies to all tenants on next check.`);
      onSaved?.();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="vb-card p-5" data-testid={`checklist-tpl-${template.code}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex-1 min-w-[240px]">
          <div className="text-[11px] uppercase tracking-wider opacity-50 mb-1">{template.code}</div>
          <input
            className="vb-input" value={title} maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            data-testid={`tpl-title-${template.code}`}
            placeholder="Checklist title"
          />
          <div className="mt-2 flex items-center gap-2">
            <label className="text-xs opacity-60 whitespace-nowrap">Form ref</label>
            <input
              className="vb-input" value={formRef} maxLength={80}
              onChange={(e) => setFormRef(e.target.value)}
              data-testid={`tpl-formref-${template.code}`}
              placeholder="e.g. VB-PRP-07-(F01)"
              style={{ maxWidth: 260 }}
            />
          </div>
        </div>
        <span className="vb-pill ok text-[11px]">{activeCount} active item{activeCount === 1 ? "" : "s"}</span>
      </div>

      <div className="space-y-5">
        {sections.map((s, si) => (
          <div key={si} className="rounded-lg p-3" style={{ background: "var(--vb-bg-2)" }} data-testid={`tpl-group-${template.code}-${si}`}>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <input
                className="vb-input" value={s.name} maxLength={120}
                onChange={(e) => setGroupName(si, { name: e.target.value })}
                placeholder="Group name (English)"
                data-testid={`tpl-group-name-${template.code}-${si}`}
                style={{ maxWidth: 260 }}
              />
              <input
                className="vb-input" value={s.name_zh}
                onChange={(e) => setGroupName(si, { name_zh: e.target.value })}
                placeholder="组名 (中文)"
                data-testid={`tpl-group-name-zh-${template.code}-${si}`}
                style={{ maxWidth: 200 }}
              />
              {s.sub_area && <span className="vb-pill text-[10px] opacity-70">{s.sub_area}</span>}
            </div>

            <div className="space-y-2">
              {s.items.map((it, ii) => (
                <div
                  key={ii}
                  className="flex items-center gap-2 flex-wrap p-2 rounded-md"
                  style={{ background: "var(--vb-bg)", opacity: it.is_active ? 1 : 0.55 }}
                  data-testid={`tpl-item-${template.code}-${si}-${ii}`}
                >
                  <input
                    className="vb-input flex-1 min-w-[180px]" value={it.label} maxLength={200}
                    onChange={(e) => setItem(si, ii, { label: e.target.value })}
                    placeholder="Item label (English)"
                    data-testid={`tpl-item-label-${template.code}-${si}-${ii}`}
                  />
                  <input
                    className="vb-input" value={it.label_zh}
                    onChange={(e) => setItem(si, ii, { label_zh: e.target.value })}
                    placeholder="标签 (中文)"
                    data-testid={`tpl-item-label-zh-${template.code}-${si}-${ii}`}
                    style={{ maxWidth: 180 }}
                  />
                  <button
                    type="button"
                    className={`vb-pill ${it.is_active ? "ok" : "warn"}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setItem(si, ii, { is_active: !it.is_active })}
                    data-testid={`tpl-item-toggle-${template.code}-${si}-${ii}`}
                    title={it.is_active ? "Active — click to deactivate" : "Inactive — click to activate"}
                  >
                    {it.is_active ? <><Eye size={12} /> Active</> : <><EyeOff size={12} /> Inactive</>}
                  </button>
                  {showRequiresReason && (
                    <button
                      type="button"
                      className={`vb-pill ${it.requires_reason ? "warn" : ""}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => setItem(si, ii, { requires_reason: !it.requires_reason })}
                      data-testid={`tpl-item-reason-${template.code}-${si}-${ii}`}
                      title="If ticked, visitor must type a reason when they answer Yes"
                    >
                      Requires reason on Yes: {it.requires_reason ? "Yes" : "No"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="vb-btn ghost text-xs"
                    onClick={() => {
                      const label = (it.label || "").trim() || "this item";
                      if (window.confirm(`Remove "${label}"?\n\nThis just stages the removal — nothing is persisted until you click "Save changes". Past records already signed off remain unchanged.`)) {
                        removeItem(si, ii);
                      }
                    }}
                    data-testid={`tpl-item-remove-${template.code}-${si}-${ii}`}
                    title="Remove item (asks for confirmation; persisted on Save)"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button" className="vb-btn ghost text-xs mt-2"
              onClick={() => addItem(si)}
              data-testid={`tpl-add-item-${template.code}-${si}`}
            >
              <Plus size={13} /> Add item
            </button>
          </div>
        ))}
      </div>

      {err && <div className="vb-pill err mt-4" data-testid={`tpl-err-${template.code}`}>{err}</div>}

      <div className="flex items-center gap-3 mt-4">
        <button className="vb-btn" onClick={save} disabled={busy} data-testid={`tpl-save-${template.code}`}>
          <Save size={14} /> {busy ? "Saving…" : "Save changes"}
        </button>
        <button className="vb-btn ghost" onClick={reset} disabled={busy} data-testid={`tpl-reset-${template.code}`}>
          <RotateCcw size={13} /> Reset
        </button>
      </div>
    </div>
  );
}
